import { Injectable } from '@angular/core';
import { Coordinate } from '@kinomap-dev/ng-ui-lib/core';
import { BehaviorSubject } from 'rxjs';
import { Buffer } from 'buffer';

@Injectable({
  providedIn: 'root',
})
export class GpsService {
  private degToRadius(deg: number): number {
    return deg * (Math.PI / 180);
  }

  private rad2deg(deg: number): number {
    return deg * (180 / Math.PI);
  }

  private getDistanceBetween(
    coordinate_A: { lat: number; lon: number },
    coordinate_B: { lat: number; lon: number }
  ): number {
    const angle: number = coordinate_A.lon - coordinate_B.lon;
    let dist: number =
      Math.sin(this.degToRadius(coordinate_A.lat)) * Math.sin(this.degToRadius(coordinate_B.lat)) +
      Math.cos(this.degToRadius(coordinate_A.lat)) *
      Math.cos(this.degToRadius(coordinate_B.lat)) *
      Math.cos(this.degToRadius(angle));
    dist = Math.acos(dist);
    dist = this.rad2deg(dist);
    const meters: number = dist * 60 * 1.1515 * 1609.344;
    return isNaN(meters) ? 0 : meters;
  }

  private calculateDistance(
    index: number,
    coordinates: Coordinate[],
    lat: number,
    lon: number
  ): number {
    if (index === 0) {
      return 0;
    }
    return (
      coordinates[index - 1].distance +
      this.getDistanceBetween(
        { lat, lon },
        { lat: coordinates[index - 1].latitude, lon: coordinates[index - 1].longitude }
      )
    );
  }

  private calculateSpeed(
    index: number,
    coordinates: Coordinate[],
    distance: number,
    time: number
  ): number {
    if (index === 0) {
      return 0;
    }
    const prev = coordinates[index - 1];
    const deltaTime = time - prev.coo_time;
    let speed: number;
    if (deltaTime > 0) {
      speed = ((distance - prev.distance) / deltaTime) * 3.6;
    } else {
      speed = prev.speed;
    }
    if (!isFinite(speed) || speed == null) {
      speed = prev.speed;
    }
    return speed;
  }

  private async parseFitFile(file: File): Promise<BehaviorSubject<Coordinate[]>> {
    const { default: FitParser } = await import('fit-file-parser');
    const arrayBuffer = await file.arrayBuffer();
    const parser = new FitParser({
      force: true,
      speedUnit: 'km/h',
      lengthUnit: 'm',
      elapsedRecordField: true,
      mode: 'list',
    });
    const result = await parser.parseAsync(Buffer.from(arrayBuffer));
    const records: any[] = (result as any).records ?? [];
    const coordinates: Coordinate[] = [];
    for (let i = 0; i < records.length; i++) {
      const rec = records[i];
      if (rec.position_lat == null || rec.position_long == null) continue;
      const lat: number = rec.position_lat;
      const lon: number = rec.position_long;
      const time: number = rec.timestamp instanceof Date ? rec.timestamp.getTime() / 1000 : 0;
      const distance: number = this.calculateDistance(coordinates.length, coordinates, lat, lon);
      const speed: number = rec.speed ?? this.calculateSpeed(coordinates.length, coordinates, distance, time);
      coordinates.push({
        distance,
        elevation: rec.altitude ?? NaN,
        latitude: lat,
        longitude: lon,
        position: coordinates.length,
        speed,
        coo_time: time,
        hearth_rate: rec.heart_rate,
        cadence: rec.cadence,
        power: rec.power,
      });
    }
    return new BehaviorSubject<Coordinate[]>(coordinates);
  }

  async formatXmlToCoordinates(file: File): Promise<BehaviorSubject<Coordinate[]>> {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (ext === 'fit') {
      return this.parseFitFile(file);
    }
    let coordinates: Coordinate[] = [];
    const domParser: DOMParser = new DOMParser();
    const fileContent: string = await file.text();
    const doc: XMLDocument = domParser.parseFromString(fileContent, 'text/xml');
    const allTrackPoint: NodeListOf<Element> = doc.querySelectorAll('trkpt');
    const allElevation: NodeListOf<Element> = doc.querySelectorAll('ele');
    const allTime: NodeListOf<Element> = doc.querySelectorAll('time');
    allTrackPoint.forEach((trackPoint: Element, index: number) => {
      const lat: number = parseFloat(trackPoint.getAttribute('lat')!);
      const lon: number = parseFloat(trackPoint.getAttribute('lon')!);
      const elevation: number = parseFloat(allElevation[index].innerHTML);
      const distance: number = this.calculateDistance(index, coordinates, lat, lon);
      const time: number = new Date(allTime[index].innerHTML).getTime() / 1000;
      const speed = this.calculateSpeed(index, coordinates, distance, time);

      // Parse additional data from <extensions> (Garmin TrackPointExtension / PowerExtension)
      // Namespace-agnostic lookup via '*' handles any prefix (gpxtpx:, ns3:, etc.)
      let heart_rate: number | undefined;
      let cadence: number | undefined;
      let power: number | undefined;
      const extensions = trackPoint.querySelector('extensions');
      if (extensions) {
        const hrEl = extensions.getElementsByTagNameNS('*', 'hr')[0];
        if (hrEl?.textContent) {
          const v = parseFloat(hrEl.textContent);
          if (!isNaN(v)) heart_rate = v;
        }
        const cadEl = extensions.getElementsByTagNameNS('*', 'cad')[0];
        if (cadEl?.textContent) {
          const v = parseFloat(cadEl.textContent);
          if (!isNaN(v)) cadence = v;
        }
        const powerEl = extensions.getElementsByTagNameNS('*', 'PowerInWatts')[0];
        if (powerEl?.textContent) {
          const v = parseFloat(powerEl.textContent);
          if (!isNaN(v)) power = v;
        }
      }

      const coordinate: Coordinate = {
        distance: distance,
        elevation: elevation,
        latitude: lat,
        longitude: lon,
        position: index,
        speed: speed,
        coo_time: time,
        hearth_rate: heart_rate,
        cadence,
        power,
      };
      coordinates.push(coordinate);
    });
    return new BehaviorSubject<Coordinate[]>(coordinates);
  }

  getRecordedDate(coordinates: Coordinate[]): Date {
    return new Date(coordinates[0].coo_time * 1000);
  }
}
